import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches React render errors and shows a recovery UI instead of a white screen.
 * Wrap route-level components (especially public-facing pages) with this boundary.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Log to console so developers can see what went wrong
    console.error("[ErrorBoundary] Render error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
            fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true" style={{ marginBottom: "1rem" }}>
            <circle cx="24" cy="24" r="23" stroke="#a4262c" strokeWidth="2" />
            <path d="M24 14v13M24 31v2" stroke="#a4262c" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
          <h2 style={{ color: "#201f1e", margin: "0 0 0.5rem", fontSize: "1.25rem", fontWeight: 600 }}>
            Something went wrong
          </h2>
          <p style={{ color: "#605e5c", margin: "0 0 1.5rem", maxWidth: "420px", lineHeight: 1.5 }}>
            {this.state.error?.message
              ? `Error: ${this.state.error.message}`
              : "An unexpected error occurred while loading the page."}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1.25rem",
              background: "#1a7b7e",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.9375rem",
              fontWeight: 500,
            }}
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
